import { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import Button from '../../components/base/Button';
import { useBooking } from '../../context/BookingContext';
import axios from 'axios';
const API = import.meta.env.VITE_API_URL || '/api';
// ─── Types ────────────────────────────────────────────────────────────────────
interface Destination {
  id: number;
  name: string;
  country: string;
  region: string;
  price: number;
  description: string;
  budget: string;
  visa_free: boolean;
  image_url: string;
  is_active: boolean;
}

// ─── Airport Autocomplete Input ───────────────────────────────────────────────
function AirportInput({
  value, onChange, placeholder, icon
}: { value: string; onChange: (val: string, iata: string) => void; placeholder: string; icon: string }) {
  const [query, setQuery] = useState(value || '');
  const [results, setResults] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (query.length < 2) { setResults([]); setShow(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${API}/travel/airports?q=${encodeURIComponent(query)}`);
        setResults(res.data || []);
        setShow(true);
      } catch { setResults([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  const select = (airport: any) => {
    const label = `${airport.city} (${airport.iata})`;
    setQuery(label);
    onChange(label, airport.iata);
    setShow(false);
  };

  return (
    <div ref={ref} className="relative">
      <i className={`${icon} absolute left-4 top-1/2 -translate-y-1/2 text-logo-gold-500 text-xl z-10`}></i>
      <input
        value={query}
        onChange={e => { setQuery(e.target.value); onChange(e.target.value, e.target.value); }}
        placeholder={placeholder}
        className="w-full bg-white text-slate-900 pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm"
      />
      {show && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden">
          {results.map((a: any) => (
            <button
              key={a.iata}
              type="button"
              onClick={() => select(a)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0"
            >
              <span className="font-bold text-logo-gold-600 text-sm w-10">{a.iata}</span>
              <div>
                <p className="font-semibold text-slate-800 text-sm">{a.city}</p>
                <p className="text-xs text-slate-400">{a.name} · {a.country}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Hotel Destination Input ──────────────────────────────────────────────────
function HotelDestInput({
  value, onSelect
}: { value: string; onSelect: (name: string, id: number) => void }) {
  const [q, setQ] = useState(value || '');
  const [results, setResults] = useState<any[]>([]);
  const [show, setShow] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => { if (ref.current && !ref.current.contains(e.target as Node)) setShow(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (q.length < 2) { setResults([]); setShow(false); return; }
    const t = setTimeout(async () => {
      try {
        const res = await axios.post(`${API}/travel/hotel-destinations`, { text: q });
        setResults(res.data?.results || []);
        setShow(true);
      } catch { setResults([]); }
    }, 400);
    return () => clearTimeout(t);
  }, [q]);

  const select = (city: any) => {
    const name = city.Name || city.name;
    setQ(name);
    onSelect(name, city.Id || city.id);
    setShow(false);
  };

  return (
    <div ref={ref} className="relative">
      <i className="ri-hotel-line absolute left-4 top-1/2 -translate-y-1/2 text-logo-gold-500 text-xl z-10"></i>
      <input
        value={q}
        onChange={e => { setQ(e.target.value); }}
        placeholder="Destination city (e.g. Lagos)"
        className="w-full bg-white text-slate-900 pl-12 pr-4 py-4 rounded-xl border border-slate-200 focus:outline-none focus:ring-2 focus:ring-logo-gold-400 shadow-sm"
      />
      {show && results.length > 0 && (
        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-2xl border border-slate-100 overflow-hidden">
          {results.map((c: any, i: number) => (
            <button
              key={i}
              type="button"
              onClick={() => select(c)}
              className="w-full text-left px-4 py-3 hover:bg-slate-50 flex items-center gap-3 border-b border-slate-50 last:border-0"
            >
              <i className="ri-map-pin-line text-logo-gold-500"></i>
              <span className="font-semibold text-slate-800 text-sm">{c.Name || c.name}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────
export default function GeneralPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addToCart } = useBooking();

  // Destination filters
  const [destinations, setDestinations] = useState<Destination[]>([]);
  const [destLoading, setDestLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRegion, setSelectedRegion] = useState('all');
  const [selectedBudget, setSelectedBudget] = useState('all');
  const [showVisaFreeOnly, setShowVisaFreeOnly] = useState(false);

  // Flight search
  const [flightType, setFlightType] = useState('one-way');
  const [flightReturnDate, setFlightReturnDate] = useState('');
  const [flightFrom, setFlightFrom] = useState('');
  const [flightFromIata, setFlightFromIata] = useState('');
  const [flightTo, setFlightTo] = useState('');
  const [flightToIata, setFlightToIata] = useState('');
  const [flightDate, setFlightDate] = useState('');
  const [flightPassengers, setFlightPassengers] = useState(1);
  const [flightCabinClass, setFlightCabinClass] = useState('economy');
  const [flightResults, setFlightResults] = useState<any[]>([]);
  const [flightLoading, setFlightLoading] = useState(false);
  const [flightSearched, setFlightSearched] = useState(false);
  const [flightError, setFlightError] = useState('');

  // Hotel search
  const [hotelDestName, setHotelDestName] = useState('');
  const [hotelCityId, setHotelCityId] = useState<number | null>(null);
  const [hotelCheckIn, setHotelCheckIn] = useState('');
  const [hotelCheckOut, setHotelCheckOut] = useState('');
  const [hotelGuests, setHotelGuests] = useState(2);
  const [hotelStars, setHotelStars] = useState('all');
  const [hotelResults, setHotelResults] = useState<any[]>([]);
  const [hotelLoading, setHotelLoading] = useState(false);
  const [hotelSearched, setHotelSearched] = useState(false);
  const [hotelError, setHotelError] = useState('');

  const [activeTab, setActiveTab] = useState<'destinations' | 'flights' | 'hotels'>('destinations');

  // Check for visa-free filter
  useEffect(() => {
    if (searchParams.get('filter') === 'visa-free') setShowVisaFreeOnly(true);
  }, [searchParams]);

  // Fetch destinations from DB
  useEffect(() => {
    const fetchDests = async () => {
      try {
        const res = await axios.get(`${API}/destinations`);
        setDestinations(res.data || []);
      } catch {
        console.error('Failed to fetch destinations');
      } finally {
        setDestLoading(false);
      }
    };
    fetchDests();
  }, []);

  const filteredDestinations = destinations.filter(d => {
    const q = searchTerm.toLowerCase();
    return (
      (d.name.toLowerCase().includes(q) || d.country.toLowerCase().includes(q) || d.description.toLowerCase().includes(q)) &&
      (selectedRegion === 'all' || d.region === selectedRegion) &&
      (selectedBudget === 'all' || d.budget === selectedBudget) &&
      (!showVisaFreeOnly || d.visa_free)
    );
  });

  const regions = ['all', ...Array.from(new Set(destinations.map(d => d.region).filter(Boolean)))];

  // Flight search handler
  const handleFlightSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    const origin = flightFromIata || flightFrom;
    const destination = flightToIata || flightTo;
    if (!origin || !destination || !flightDate) { setFlightError('Please fill all flight fields.'); return; }
    if (flightType === 'round-trip' && !flightReturnDate) { setFlightError('Please select a return date.'); return; }
    setFlightLoading(true); setFlightError(''); setFlightSearched(true); setFlightResults([]);
    try {
      const payload: any = { origin, destination, date: flightDate, passengers: flightPassengers, cabin_class: flightCabinClass };
      if (flightType === 'round-trip') payload.return_date = flightReturnDate;
      const res = await axios.post(`${API}/travel/search-flights`, payload);
      setFlightResults(res.data?.results || []);
      if (!res.data?.results?.length) setFlightError('No flights found for this route. Try different dates or airports.');
    } catch (err: any) {
      setFlightError('Could not fetch flights. Please try again.');
    } finally { setFlightLoading(false); }
  };

  // Hotel search handler
  const handleHotelSearch = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!hotelDestName || !hotelCheckIn || !hotelCheckOut) { setHotelError('Please fill all hotel fields.'); return; }
    setHotelLoading(true); setHotelError(''); setHotelSearched(true); setHotelResults([]);
    try {
      const res = await axios.post(`${API}/travel/search-hotels`, {
        city_id: hotelCityId, city_name: hotelDestName,
        destination: hotelDestName, checkIn: hotelCheckIn, checkOut: hotelCheckOut, guests: hotelGuests, stars: hotelStars
      });
      setHotelResults(res.data?.results || []);
      if (!res.data?.results?.length) setHotelError('No hotels found. Try a different city or dates.');
    } catch { setHotelError('Could not fetch hotels. Please try again.'); }
    finally { setHotelLoading(false); }
  };

  const formatPrice = (n: number) => `₦${n.toLocaleString()}`;

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      <Header />

      {/* Hero */}
      <div className="relative h-72 md:h-96 bg-cover bg-center bg-no-repeat" style={{ backgroundImage: `url('https://images.unsplash.com/photo-1476514525535-07fb3b4ae5f1?auto=format&fit=crop&w=1400&q=80')` }}>
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900/60 to-slate-900/80"></div>
        <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-4">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white mb-4 tracking-tight">
            {showVisaFreeOnly ? 'Visa-Free Destinations' : 'Explore the World'}
          </h1>
          <p className="text-xl text-white/80 max-w-2xl">
            {showVisaFreeOnly ? 'Travel without visa hassles — amazing countries await.' : 'Search flights, book hotels, and discover popular destinations.'}
          </p>
        </div>
      </div>

      {/* Tab Bar */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-30 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 flex overflow-x-auto hide-scrollbar">
          {(['destinations', 'flights', 'hotels'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 md:px-6 py-4 font-bold text-sm capitalize border-b-2 transition-colors whitespace-nowrap ${activeTab === tab ? 'border-logo-gold-600 text-logo-gold-600' : 'border-transparent text-slate-500 hover:text-slate-800'}`}
            >
              <i className={`${tab === 'destinations' ? 'ri-compass-3-line' : tab === 'flights' ? 'ri-plane-line' : 'ri-hotel-line'} mr-2`}></i>
              {tab}
            </button>
          ))}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-10">

        {/* ─── DESTINATIONS TAB ─────────────────────────────────── */}
        {activeTab === 'destinations' && (
          <>
            {/* Visa-free banner */}
            {showVisaFreeOnly && (
              <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8 flex gap-4 items-start">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                  <i className="ri-passport-line text-2xl text-green-600"></i>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-green-900 mb-1">Travel Visa-Free with Your Nigerian Passport</h3>
                  <p className="text-green-700 text-sm">Nigerian passport holders can visit 30+ destinations without a visa.</p>
                </div>
              </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 md:p-6 mb-8">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="relative">
                  <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                  <input placeholder="Search destinations..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="w-full pl-9 pr-4 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-logo-gold-400 focus:border-logo-gold-400 text-sm" />
                </div>
                <select value={selectedRegion} onChange={e => setSelectedRegion(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-sm">
                  {regions.map(r => <option key={r} value={r}>{r === 'all' ? 'All Regions' : r}</option>)}
                </select>
                <select value={selectedBudget} onChange={e => setSelectedBudget(e.target.value)} className="w-full px-3 py-2.5 border border-slate-300 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-sm">
                  <option value="all">All Budgets</option>
                  <option value="budget">Budget</option>
                  <option value="mid-range">Mid-Range</option>
                  <option value="luxury">Luxury</option>
                </select>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input type="checkbox" checked={showVisaFreeOnly} onChange={e => setShowVisaFreeOnly(e.target.checked)} className="accent-logo-gold-500 w-4 h-4" />
                  <span className="text-sm font-medium text-slate-700">Visa-Free Only</span>
                </label>
              </div>
            </div>

            {/* Results count */}
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                {showVisaFreeOnly ? 'Visa-Free Destinations' : 'Popular Destinations'}
                <span className="text-slate-400 font-normal ml-2 text-lg">({filteredDestinations.length})</span>
              </h2>
            </div>

            {/* Destinations Grid */}
            {destLoading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {[...Array(6)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl overflow-hidden animate-pulse shadow-sm border border-slate-100">
                    <div className="h-48 bg-slate-200"></div>
                    <div className="p-5 space-y-3">
                      <div className="h-5 bg-slate-200 rounded w-2/3"></div>
                      <div className="h-4 bg-slate-100 rounded w-full"></div>
                      <div className="h-4 bg-slate-100 rounded w-4/5"></div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredDestinations.map(dest => (
                  <div key={dest.id} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group">
                    <div className="relative h-52 overflow-hidden">
                      <img src={dest.image_url} alt={dest.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                      {dest.visa_free && (
                        <span className="absolute top-3 left-3 bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                          <i className="ri-passport-line"></i> Visa-Free
                        </span>
                      )}
                      <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-slate-800 text-xs font-bold px-3 py-1 rounded-full capitalize">{dest.budget}</span>
                    </div>
                    <div className="p-5">
                      <div className="flex justify-between items-start mb-2">
                        <div>
                          <h3 className="text-xl font-bold text-slate-900">{dest.name}</h3>
                          <p className="text-slate-500 text-sm">{dest.region}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xl font-extrabold text-logo-gold-600">{formatPrice(dest.price)}</p>
                          <p className="text-xs text-slate-400">per person</p>
                        </div>
                      </div>
                      <p className="text-slate-500 text-sm mb-4 leading-relaxed">{dest.description}</p>
                      <div className="grid grid-cols-3 gap-2">
                        <button
                          onClick={() => { setActiveTab('hotels'); setHotelDestName(dest.name); }}
                          className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all"
                        >
                          <i className="ri-hotel-line"></i> Hotels
                        </button>
                        <button
                          onClick={() => { setActiveTab('flights'); setFlightTo(dest.name); }}
                          className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all"
                        >
                          <i className="ri-plane-line"></i> Flights
                        </button>
                        <button
                          onClick={() => navigate(`/cars?destination=${dest.name}`)}
                          className="flex items-center justify-center gap-1 text-xs font-semibold py-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-800 hover:text-white hover:border-slate-800 transition-all"
                        >
                          <i className="ri-taxi-line"></i> Cars
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
                {filteredDestinations.length === 0 && (
                  <div className="col-span-3 text-center py-20">
                    <i className="ri-search-line text-7xl text-slate-200 mb-4 block"></i>
                    <h3 className="text-xl font-bold text-slate-700">No destinations found</h3>
                    <p className="text-slate-400 mt-2">Try adjusting your filters</p>
                  </div>
                )}
              </div>
            )}
          </>
        )}

        {/* ─── FLIGHTS TAB ──────────────────────────────────────── */}
        {activeTab === 'flights' && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 md:p-8 mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Search Flights</h2>
              <form onSubmit={handleFlightSearch} className="space-y-5">
                <div className="flex space-x-6 mb-4 text-sm font-semibold text-slate-700">
                  <label className="flex items-center cursor-pointer hover:text-logo-gold-600 transition-colors">
                    <input type="radio" name="g_flightType" value="one-way" checked={flightType === 'one-way'} onChange={(e) => setFlightType(e.target.value)} className="mr-2 accent-logo-gold-500" />
                    One-way
                  </label>
                  <label className="flex items-center cursor-pointer hover:text-logo-gold-600 transition-colors">
                    <input type="radio" name="g_flightType" value="round-trip" checked={flightType === 'round-trip'} onChange={(e) => setFlightType(e.target.value)} className="mr-2 accent-logo-gold-500" />
                    Round-trip
                  </label>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">From</label>
                    <AirportInput value={flightFrom} onChange={(v, iata) => { setFlightFrom(v); setFlightFromIata(iata); }} placeholder="Origin airport or city" icon="ri-takeoff-line" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">To</label>
                    <AirportInput value={flightTo} onChange={(v, iata) => { setFlightTo(v); setFlightToIata(iata); }} placeholder="Destination airport or city" icon="ri-flight-land-line" />
                  </div>
                </div>
                <div className={`grid grid-cols-1 gap-5 ${flightType === 'round-trip' ? 'md:grid-cols-5' : 'md:grid-cols-4'}`}>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Departure Date</label>
                    <input type="date" value={flightDate} onChange={e => setFlightDate(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  {flightType === 'round-trip' && (
                    <div>
                      <label className="block text-sm font-semibold text-slate-700 mb-2">Return Date</label>
                      <input type="date" value={flightReturnDate} onChange={e => setFlightReturnDate(e.target.value)} min={flightDate || new Date().toISOString().split('T')[0]} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                    </div>
                  )}
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Cabin Class</label>
                    <select value={flightCabinClass} onChange={e => setFlightCabinClass(e.target.value)} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800 bg-white">
                      <option value="economy">Economy</option>
                      <option value="premium_economy">Premium Economy</option>
                      <option value="business">Business</option>
                      <option value="first">First Class</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Passengers</label>
                    <input type="number" min={1} max={9} value={flightPassengers} onChange={e => setFlightPassengers(Number(e.target.value))} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  <div className="flex items-end">
                    <Button type="submit" disabled={flightLoading} className="w-full bg-logo-gold-600 hover:bg-logo-gold-700 text-white font-bold py-4 rounded-xl text-lg">
                      {flightLoading ? 'Searching...' : 'Search Flights'}
                    </Button>
                  </div>
                </div>
                {flightError && <p className="text-red-600 text-sm font-medium">{flightError}</p>}
              </form>
            </div>

            {flightLoading && (
              <div className="flex flex-col items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-gold-600 mb-4"></div>
                <p className="text-slate-500 font-medium">Searching live flights...</p>
              </div>
            )}

            {!flightLoading && flightSearched && flightResults.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-900">{flightResults.length} Flights Found</h3>
                {flightResults.map((flight: any, i: number) => (
                  <div key={i} className="bg-white rounded-2xl p-5 md:p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow flex flex-col md:flex-row justify-between gap-6">
                    <div className="flex items-center gap-4 md:gap-6">
                      <div className="w-12 h-12 md:w-14 md:h-14 rounded-xl bg-slate-100 flex items-center justify-center flex-shrink-0">
                        <i className="ri-plane-fill text-xl md:text-2xl text-slate-500"></i>
                      </div>
                      <div>
                        <p className="font-bold text-slate-900 text-base md:text-lg">{flight.airline}</p>
                        <p className="text-slate-500 text-xs md:text-sm capitalize">{flight.cabin_class || 'Economy'} · {flight.stops === 0 ? 'Direct' : `${flight.stops} stop${flight.stops > 1 ? 's' : ''}`}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4 md:gap-8 flex-1 justify-between md:justify-center w-full md:w-auto px-2 md:px-0">
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-900">{flight.departureTime || '--:--'}</p>
                        <p className="text-xs text-slate-400 uppercase">{flight.origin}</p>
                      </div>
                      <div className="flex flex-col items-center">
                        <p className="text-xs text-slate-400">{flight.duration || '--'}</p>
                        <div className="w-24 h-px bg-slate-300 relative my-1">
                          <i className="ri-plane-fill text-logo-gold-500 absolute top-1/2 left-1/2 -translate-y-1/2 -translate-x-1/2 text-sm"></i>
                        </div>
                        <p className="text-xs text-slate-400">{flight.stops === 0 ? 'Direct' : `${flight.stops} stop`}</p>
                      </div>
                      <div className="text-center">
                        <p className="text-2xl font-bold text-slate-900">{flight.arrivalTime || '--:--'}</p>
                        <p className="text-xs text-slate-400 uppercase">{flight.destination}</p>
                      </div>
                    </div>
                    <div className="flex flex-col items-start md:items-end justify-center gap-2 mt-4 md:mt-0 border-t border-slate-100 md:border-0 pt-4 md:pt-0">
                      <div className="flex flex-row md:flex-col items-baseline md:items-end gap-2 md:gap-0">
                        <p className="text-2xl font-extrabold text-logo-gold-600">{formatPrice(flight.retailPrice)}</p>
                        <p className="text-xs text-slate-400">per person</p>
                      </div>
                      <button
                        className="bg-slate-900 hover:bg-logo-gold-600 text-white font-bold px-5 py-2.5 rounded-xl transition-colors text-sm"
                        onClick={() => {
                          addToCart({
                            type: 'flight',
                            price: flight.retailPrice,
                            rawPriceUSD: flight.rawPriceUSD,
                            search_id: flight.search_id,
                            offer_id: flight.offer_id || flight.id,
                            details: {
                              airline: flight.airline,
                              origin: flight.origin,
                              destination: flight.destination,
                              departureTime: flight.departureTime,
                              arrivalTime: flight.arrivalTime,
                              duration: flight.duration,
                              stops: flight.stops,
                              cabin_class: flight.cabin_class,
                              booking_url: flight.booking_url
                            }
                          });
                          navigate('/booking/checkout');
                        }}
                      >
                        Book Now
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── HOTELS TAB ───────────────────────────────────────── */}
        {activeTab === 'hotels' && (
          <>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-5 md:p-8 mb-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Search Hotels</h2>
              <form onSubmit={handleHotelSearch} className="space-y-5">
                <div>
                  <label className="block text-sm font-semibold text-slate-700 mb-2">Destination City</label>
                  <HotelDestInput value={hotelDestName} onSelect={(name, id) => { setHotelDestName(name); setHotelCityId(id); }} />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-5">
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Check-in</label>
                    <input type="date" value={hotelCheckIn} onChange={e => setHotelCheckIn(e.target.value)} min={new Date().toISOString().split('T')[0]} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Check-out</label>
                    <input type="date" value={hotelCheckOut} onChange={e => setHotelCheckOut(e.target.value)} min={hotelCheckIn || new Date().toISOString().split('T')[0]} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Guests</label>
                    <input type="number" min={1} max={10} value={hotelGuests} onChange={e => setHotelGuests(Number(e.target.value))} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800" />
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-slate-700 mb-2">Star Rating</label>
                    <select value={hotelStars} onChange={e => setHotelStars(e.target.value)} className="w-full px-4 py-4 border border-slate-200 rounded-xl focus:ring-2 focus:ring-logo-gold-400 text-slate-800 bg-white">
                      <option value="all">Any Rating</option>
                      <option value="5">5 Stars</option>
                      <option value="4">4+ Stars</option>
                      <option value="3">3+ Stars</option>
                    </select>
                  </div>
                </div>
                <Button type="submit" disabled={hotelLoading} className="w-full bg-logo-gold-600 hover:bg-logo-gold-700 text-white font-bold py-4 rounded-xl text-lg">
                  {hotelLoading ? 'Searching...' : 'Search Hotels'}
                </Button>
                {hotelError && <p className="text-red-600 text-sm font-medium">{hotelError}</p>}
              </form>
            </div>

            {hotelLoading && (
              <div className="flex flex-col items-center py-20">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-logo-gold-600 mb-4"></div>
                <p className="text-slate-500 font-medium">Searching live hotel inventory...</p>
              </div>
            )}

            {!hotelLoading && hotelSearched && hotelResults.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-xl font-bold text-slate-900">{hotelResults.length} Hotels Found</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {hotelResults.map((hotel: any, i: number) => (
                    <div key={i} className="bg-white rounded-2xl overflow-hidden shadow-sm border border-slate-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                      <div className="h-48 overflow-hidden relative">
                        <img src={hotel.image || hotel.images?.[0] || 'https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600'} alt={hotel.name} className="w-full h-full object-cover" />
                        {hotel.stars && (
                          <span className="absolute top-3 right-3 bg-white/90 backdrop-blur-sm text-logo-gold-600 text-xs font-bold px-2 py-1 rounded-full">
                            {'★'.repeat(Math.min(hotel.stars, 5))}
                          </span>
                        )}
                      </div>
                      <div className="p-5">
                        <h4 className="font-bold text-slate-900 text-lg mb-1">{hotel.name}</h4>
                        {hotel.address && <p className="text-slate-500 text-sm mb-3 flex items-center gap-1"><i className="ri-map-pin-line text-logo-gold-500"></i>{hotel.address}</p>}
                        {hotel.board && <p className="text-xs text-slate-500 mb-3">{hotel.board}</p>}
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-xl font-extrabold text-logo-gold-600">{formatPrice(hotel.retailPrice)}</p>
                            <p className="text-xs text-slate-400">per night</p>
                          </div>
                          <button
                            className="bg-slate-900 hover:bg-logo-gold-600 text-white font-bold px-4 py-2 rounded-xl text-sm transition-colors"
                            onClick={() => {
                              addToCart({
                                type: 'hotel',
                                price: hotel.retailPrice,
                                rawPriceUSD: hotel.rawPriceUSD,
                                search_id: hotel.session_id,
                                offer_id: hotel.id,
                                session_id: hotel.session_id,
                                details: {
                                  name: hotel.name,
                                  address: hotel.address,
                                  stars: hotel.stars,
                                  board: hotel.board,
                                  image: hotel.image
                                }
                              });
                              navigate('/booking/checkout');
                            }}
                          >
                            Book Now
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      <Footer />
    </div>
  );
}